import { useCallback, useState } from 'react';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { showNotification } from 'app/providers/NotificationsProvider';
import { Invoice, InvoiceItem } from 'entities/Invoice';
import { useIssueUserInvoiceMutation } from 'entities/User';

interface HookArgs {
  userId: string | undefined;
  warehouseId: string | undefined;
  invoiceData: Nullable<Partial<Invoice>> | undefined;
  invoiceItems: InvoiceItem[];
  onSuccess?: () => void;
}

interface HookApi {
  isOpenedConfirm: boolean;
  isLoading: boolean;
  handleIssueUserInvoice: () => void;
  openConfirmModal: () => void;
  closeConfirmModal: () => void;
}

export const useIssuePersonalInvoice = ({ invoiceData, invoiceItems, userId, warehouseId, onSuccess }: HookArgs): HookApi => {
  const [isOpenedConfirm, setOpenedConfirm] = useState(false);

  const { t } = useAppTranslation(['common', 'contracts']);

  const [issueUserInvoice, { isLoading }] = useIssueUserInvoiceMutation();

  const openConfirmModal = useCallback((): void => {
    setOpenedConfirm(true);
  }, []);

  const closeConfirmModal = useCallback((): void => {
    setOpenedConfirm(false);
  }, []);

  const handleIssueUserInvoice = useCallback(async (): Promise<void> => {
    try {
      if (userId && warehouseId && invoiceData && invoiceItems.length) {
        await issueUserInvoice({
          userId,
          warehouseId,
          invoiceItems,
          description: invoiceData?.description,
          dueDate: invoiceData?.dueDate,
          invoiceAddress: invoiceData?.invoiceAddress,
          invoiceEmail: invoiceData?.invoiceEmail,
        }).unwrap();

        showNotification('info', t('Success'), t('Invoice has been successfully issued', { ns: 'contracts' }));

        onSuccess?.();
      }
    } catch (error: CustomAny) {
      console.log(error);
      showNotification('error', t('Error'), t('Invoice issue error', { ns: 'contracts' }));
    } finally {
      closeConfirmModal();
    }
  }, [userId, warehouseId, t, onSuccess, closeConfirmModal, invoiceData, invoiceItems, issueUserInvoice]);

  return {
    openConfirmModal,
    closeConfirmModal,
    handleIssueUserInvoice,
    isOpenedConfirm,
    isLoading,
  };
};
